import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { TemplateSelector } from './TemplateSelector';
import { PDFExport } from './PDFExport';

interface StyleExportSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const StyleExportSheet: React.FC<StyleExportSheetProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 z-40 bg-black/40 animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Bottom sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Stile ed Export"
        className="fixed bottom-0 inset-x-0 z-50 bg-background rounded-t-3xl shadow-2xl shadow-primary/15 border-t border-border animate-spring-in-up"
        style={{ maxHeight: '85dvh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3 border-b border-border">
          <h2 className="font-serif italic text-xl font-bold text-foreground">{t('export.styleTitle', 'Stile & Export')}</h2>
          <button
            onClick={onClose}
            aria-label="Chiudi"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-secondary transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div
          className="overflow-y-auto px-5 py-4 space-y-4"
          style={{
            maxHeight: 'calc(85dvh - 6rem)',
            paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
          }}
        >
          <TemplateSelector />
          <PDFExport />
        </div>
      </div>
    </>
  );
};
